import { collection, doc, getFirestore, setDoc, addDoc, getDoc, getDocs, Timestamp } from 'firebase/firestore';
import { FirebaseProvider } from '../providers/FirebaseProvider';
import type { ICompanyConfigRepository } from '../../domain/repositories/ICompanyConfigRepository';
import type { CompanyConfig } from '../../domain/types/CompanyConfig';
import { EXPIRATION_HOURS, type ShareLinkData, type CompanyRef, type ExpirationPeriod } from '../../domain/types/Company';
import { clientTypeForRole, type CompanyRole } from '../../domain/types/Role';
import { carriersConfig, chargesConfig, customsConfig, incotermsConfig, shipmentStatusConfig, divisionsConfig, purchaseSupplierConfig, globalConfig, lastMileCarrierConfig, lastMilePickupConfig, modeOfTransportConfig, pdfConfig, quotesConfig, rateCategoryConfig, requiredFieldsConfig, serviceTypeConfig, shipmentTypeConfig, supplierConfig, internationalCarrierConfig, partiesConfig, newApprovalNotificationConfig, newDeclineNotificationConfig, newPurchaseNotificationsConfig, newShipmentNotificationConfig, confirmPurchaseOrderNotificationConfig, poDocumentUploadedNotificationConfig, consolidationConfig, chargeCategoriesConfig, shipmentDuplicateConfig } from '../config/company';
import { lastMileDeliveryPlaceConfig } from '../config/company/last-mile-delivery-place.config';

export class FirestoreCompanyConfigRepository implements ICompanyConfigRepository {
  private db;
  private defaultConfigs: Record<string, CompanyConfig>;

  constructor() {
    this.db = getFirestore(FirebaseProvider.getInstance().getApp());    
    this.defaultConfigs = {
      CARRIERS_CATALOG: carriersConfig,
      CHARGES_CATALOG: chargesConfig,
      CUSTOMS_CONFIG: customsConfig,
      INCOTERMS_CATALOG: incotermsConfig,
      SHIPMENT_STATUS: shipmentStatusConfig,
      DIVISIONS_CATALOG: divisionsConfig,
      GET_PURCHASE_SUPPLIER_CONFIG: purchaseSupplierConfig,
      GLOBAL_CONFIG: globalConfig,
      LAST_MILE_CARRIER: lastMileCarrierConfig,
      LAST_MILE_PICKUP_CATALOG: lastMilePickupConfig,
      LAST_MILE_DELIVERY_PLACE: lastMileDeliveryPlaceConfig, 
      MODE_OF_TRANSPORT: modeOfTransportConfig,
      PDF_CONFIG: pdfConfig,
      QUOTES_CONFIG: quotesConfig,
      RATE_CATEGORY_CATALOG: rateCategoryConfig,
      REQUIRED_FIELDS: requiredFieldsConfig,
      SERVICE_TYPE_CATALOG: serviceTypeConfig,
      SHIPMENT_TYPE_CATALOG: shipmentTypeConfig,
      SUPPLIER_CONFIG: supplierConfig,
      INTERNATIONAL_CARRIER: internationalCarrierConfig,
      PARTIES: partiesConfig,
      NEW_APPROVAL_NOTIFICATION: newApprovalNotificationConfig,
      NEW_DECLINE_NOTIFICATION: newDeclineNotificationConfig,
      NEW_PURCHASE_NOTIFICATIONS: newPurchaseNotificationsConfig,
      NEW_SHIPMENT_NOTIFICATION: newShipmentNotificationConfig,
      CONFIRM_PURCHASE_ORDER_NOTIFICATION: confirmPurchaseOrderNotificationConfig,
      PO_DOCUMENT_UPLOADED_NOTIFICATION: poDocumentUploadedNotificationConfig,
      CONSOLIDATION_CONFIG: consolidationConfig,
      CHARGE_CATEGORIES_CONFIG: chargeCategoriesConfig,
      SHIPMENT_DUPLICATE_CONFIG: shipmentDuplicateConfig
    };
  }

  async getDefaultConfigs(): Promise<Record<string, CompanyConfig>> {
    return this.defaultConfigs;
  }

  async initializeCompanyConfigs(companyId: string, userId: string, operationType?: 'importer' | 'forwarder'): Promise<void> {
    const configCollection = collection(this.db, 'Companies', companyId, 'config');

    const configPromises = Object.entries(this.defaultConfigs).map(([key, value]) => {
      const configDoc = doc(configCollection, key);
      let configValue = { ...value };

      if (key === 'CONSOLIDATION_CONFIG' && operationType === 'forwarder') {
        configValue = { ...configValue, enableConsolidation: true };
      }

      return setDoc(configDoc, {
        ...configValue,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: userId
      });
    });

    await Promise.all(configPromises);
  }

  async generateShareLink(companyId: string, data: ShareLinkData) {
    try {
      const shareData = {
        companyId,
        expiration: data.expiration,
        role: data.role,
        // clientType se sigue guardando para los enlaces que lea una versión anterior.
        clientType: clientTypeForRole(data.role),
        createdAt: Timestamp.now(),
        createdBy: data.createdBy,
        // Firestore rechaza undefined, así que el campo solo va si hay permisos.
        ...(data.permissions ? { permissions: data.permissions } : {})
      };

      const shareRef = await addDoc(collection(this.db, 'CompanyInvites'), shareData);
      return shareRef.id;
    } catch (error) {
      console.error('Error in FirestoreCompanyConfigRepository.generateShareLink:', error);
      throw error;
    }
  }

  async validateInviteLink(inviteId: string) {
    try {
      const inviteDoc = await getDoc(doc(this.db, 'CompanyInvites', inviteId));
      
      if (!inviteDoc.exists()) {
        throw new Error('Invitación no válida');
      }

      const inviteData = inviteDoc.data();
      const hours = EXPIRATION_HOURS[inviteData.expiration as ExpirationPeriod];

      if (hours === undefined) {
        throw new Error('Invitación no válida');
      }

      if (hours !== Infinity) {
        const expirationDate = new Date(inviteData.createdAt.toDate());
        expirationDate.setHours(expirationDate.getHours() + hours);

        if (new Date() > expirationDate) {
          throw new Error('La invitación ha expirado');
        }
      }

      // Los enlaces generados antes de los roles solo traen clientType.
      const role: CompanyRole =
        inviteData.role || (inviteData.clientType === 'view' ? 'viewer' : 'regular');

      return {
        companyId: inviteData.companyId,
        role,
        permissions: inviteData.permissions
      };
    } catch (error) {
      console.error('Error in FirestoreCompanyConfigRepository.validateInviteLink:', error);
      throw error;
    }
  }

  async getAllCompanies(): Promise<CompanyRef[]> {
    try {
      const companiesSnapshot = await getDocs(collection(this.db, 'Companies'));
      const companies: CompanyRef[] = [];

      companiesSnapshot.forEach((doc) => {
        const data = doc.data();
        companies.push({
          id: doc.id,
          name: data.name || data.companyName || 'Empresa sin nombre',
          planRef: data.planRef || null,
          membersCount: data.membersCount || 0
        });
      });

      return companies;
    } catch (error) {
      console.error('Error in FirestoreCompanyConfigRepository.getAllCompanies:', error);
      throw error;
    }
  }
} 